<template>
  <div
    class="work-schedule-calendar"
    :class="{ 'work-schedule-calendar--compact': compact }">
    <div class="work-schedule-calendar__weekdays">
      <div
        v-for="weekday in WEEKDAYS"
        :key="weekday"
        class="work-schedule-calendar__weekday">
        {{ weekday }}
      </div>
    </div>

    <div class="work-schedule-calendar__grid">
      <div
        v-for="gap in leadingGaps"
        :key="`gap-${gap}`"
        class="work-schedule-calendar__gap" />

      <a-popover
        v-for="day in days"
        :key="day.date"
        placement="bottom"
        trigger="click"
        :open="openedDate === day.date"
        @update:open="(open) => toggle(day, open)">
        <template #content>
          <div class="work-schedule-calendar__editor">
            <a-select
              :value="dayType(day)"
              :options="dayTypeOptions"
              @update:value="(value) => setType(day, value)" />

            <div
              v-if="!day.is_day_off"
              class="work-schedule-calendar__time-range">
              <a-time-picker
                :value="day.started_at"
                value-format="HH:mm"
                format="HH:mm"
                :minute-step="5"
                :allow-clear="false"
                :show-now="false"
                placeholder="Начало"
                @update:value="(value) => setTime(day, 'started_at', value)" />

              <a-time-picker
                :value="day.ended_at"
                value-format="HH:mm"
                format="HH:mm"
                :minute-step="5"
                :allow-clear="false"
                :show-now="false"
                placeholder="Окончание"
                @update:value="(value) => setTime(day, 'ended_at', value)" />
            </div>
          </div>
        </template>

        <button
          type="button"
          :disabled="!isDayEditable(day)"
          :aria-label="`${formatDate(day.date)}: ${dayHours(day) || 'не задан'}`"
          :aria-expanded="isDayEditable(day) ? openedDate === day.date : undefined"
          class="work-schedule-calendar__cell"
          :class="cellClass(day)"
          :title="dayHours(day)">
          <span class="work-schedule-calendar__number">{{ dayNumber(day) }}</span>
          <span class="work-schedule-calendar__hours work-schedule-calendar__hours--full">
            {{ cellLabel(day) }}
          </span>
          <span class="work-schedule-calendar__hours work-schedule-calendar__hours--short">
            {{ shortLabel(day) }}
          </span>
        </button>
      </a-popover>
    </div>

    <div
      v-if="!compact"
      class="work-schedule-calendar__legend"
      aria-label="Обозначения дней">
      <span
        v-for="type in DAY_TYPE_OPTIONS"
        :key="type.value"
        class="work-schedule-calendar__legend-item">
        <span
          class="work-schedule-calendar__swatch"
          :class="`work-schedule-calendar__cell--${type.value.replace('_', '-')}`"
          aria-hidden="true" />
        {{ type.label }} ({{ typeCount(type.value) }})
      </span>
      <span class="work-schedule-calendar__legend-item">
        <span
          class="work-schedule-calendar__swatch work-schedule-calendar__cell--empty"
          aria-hidden="true" />
        Не задан ({{ days.length - totals.filled_days_count }})
      </span>
      <span class="work-schedule-calendar__summary">
        <b>{{ formatDuration(totals.working_minutes) }}</b>
        · Всего дней: {{ days.length }} · Заполнено {{ totals.filled_days_count }} из {{ days.length }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import {
  DAY_TYPE, DAY_TYPE_OPTIONS, WEEKDAYS, dayType, isAbsence,
  dayHours, dayNumber, durationMinutes, formatDate, formatDuration, scheduleTotals,
} from '@/Components/CRM/WorkSchedule/workScheduleFormat';

const props = defineProps({
  days: {
    type: Array,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  canManageAbsences: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: String,
    default: null,
  },
  defaults: {
    type: Object,
    default: () => ({ started_at: '10:00', ended_at: '19:00' }),
  },
});

const emit = defineEmits(['update:days']);

const openedDate = ref(null);
const lastManualType = ref(DAY_TYPE.WORKING);
const totals = computed(() => scheduleTotals(props.days));
const typeCount = (type) => props.days.filter((day) => day.is_filled && dayType(day) === type).length;
watch(() => props.days[0]?.date, () => {
  lastManualType.value = DAY_TYPE.WORKING;
  openedDate.value = null;
});

const leadingGaps = computed(() => (props.days.length ? props.days[0].weekday - 1 : 0));

watch(() => props.editable, (editable) => {
  if (!editable) openedDate.value = null;
});

const dayTypeOptions = computed(() => DAY_TYPE_OPTIONS.filter((option) => props.canManageAbsences
  || !isAbsence({ day_type: option.value })));

const isDayEditable = (day) => props.editable
  && (!props.startDate || day.date >= props.startDate)
  && (props.canManageAbsences || !isAbsence(day));

const replace = (date, patch) => {
  emit('update:days', props.days.map((day) => {
    if (day.date !== date) return day;

    const next = { ...day, ...patch, is_filled: true };

    return {
      ...next,
      duration_minutes: next.is_day_off ? null : durationMinutes(next.started_at, next.ended_at),
    };
  }));
};

const toggle = (day, open) => {
  if (!open || !isDayEditable(day)) {
    openedDate.value = null;
    return;
  }

  if (!day.is_filled) {
    const type = dayTypeOptions.value.some((option) => option.value === lastManualType.value)
      ? lastManualType.value : DAY_TYPE.WORKING;
    const working = type === DAY_TYPE.WORKING;
    replace(day.date, {
      day_type: type,
      is_day_off: !working,
      started_at: working ? props.defaults.started_at : null,
      ended_at: working ? props.defaults.ended_at : null,
    });
    lastManualType.value = type;
  }

  openedDate.value = day.date;
};

const cellClass = (day) => ({
  'work-schedule-calendar__cell--editable': isDayEditable(day),
  'work-schedule-calendar__cell--vacation': day.is_filled && dayType(day) === DAY_TYPE.VACATION,
  'work-schedule-calendar__cell--sick-leave': day.is_filled && dayType(day) === DAY_TYPE.SICK_LEAVE,
  'work-schedule-calendar__cell--weekend': day.is_weekend,
  'work-schedule-calendar__cell--empty': !day.is_filled,
  'work-schedule-calendar__cell--day-off': day.is_filled && dayType(day) === DAY_TYPE.DAY_OFF,
  'work-schedule-calendar__cell--working': day.is_filled && !day.is_day_off,
  'work-schedule-calendar__cell--today': day.is_today,
});

const shortLabel = (day) => {
  if (!day.is_filled) return '—';

  if (day.is_day_off) {
    const labels = {
      [DAY_TYPE.DAY_OFF]: 'вых.',
      [DAY_TYPE.VACATION]: 'отп.',
      [DAY_TYPE.SICK_LEAVE]: 'бол.',
    };

    return labels[dayType(day)];
  }

  return `${day.started_at}\n${day.ended_at}`;
};

const cellLabel = (day) => (props.compact ? shortLabel(day) : dayHours(day) || 'не задан');

const setType = (day, type) => {
  if (!isDayEditable(day) || (!props.canManageAbsences && isAbsence({ day_type: type }))) return;

  lastManualType.value = type;
  const working = type === DAY_TYPE.WORKING;

  replace(day.date, {
    day_type: type,
    is_day_off: !working,
    started_at: working ? day.started_at ?? props.defaults.started_at : null,
    ended_at: working ? day.ended_at ?? props.defaults.ended_at : null,
  });
};

const setTime = (day, field, value) => {
  if (!value || !isDayEditable(day)) return;

  lastManualType.value = dayType(day);
  replace(day.date, { [field]: value });
};
</script>

<style lang="scss" scoped>
$border: 1px solid #f0f0f0;

.work-schedule-calendar {
  &__weekdays,
  &__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px;
  }

  &__weekdays {
    margin-bottom: 6px;
  }

  &__weekday {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    text-align: center;
    text-transform: uppercase;
  }

  &__gap {
    min-height: 62px;
  }

  &__swatch {
    width: 12px;
    height: 12px;
    border: $border;
    border-radius: 3px;
  }

  &__cell {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
    font: inherit;
    color: inherit;
    text-align: left;
    appearance: none;
    min-height: 62px;
    padding: 6px 8px;
    border: $border;
    border-radius: 6px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:disabled {
      cursor: default;
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid #1677ff;
      outline-offset: 2px;
    }

    &--editable {
      cursor: pointer;

      &:hover {
        border-color: #1677ff;
        box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.08);
      }
    }

    &--weekend .work-schedule-calendar__number {
      color: #cf1322;
    }

    &--empty {
      border-style: dashed;
      background: #fafafa;
    }

    &--day-off {
      border-color: #d3adf7;
      background: #f0e5ff;
    }

    &--working {
      border-color: #b7eb8f;
      background: #f6ffed;
    }

    &--vacation {
      border-color: #91caff;
      background: #e6f4ff;
    }

    &--sick-leave {
      border-color: #ffd591;
      background: #fff7e6;
    }

    &--today {
      border-color: #1677ff;
      box-shadow: inset 0 0 0 1px #1677ff;
    }
  }

  &__number {
    font-weight: 600;
    line-height: 1;
  }

  &__hours {
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
    overflow-wrap: anywhere;
  }

  &__hours--short {
    display: none;
  }

  @media (max-width: 600px) {
    &__weekdays, &__grid { gap: 3px; }
    &__cell { padding: 5px 3px; }
    &__hours--full { display: none; }
    &__hours--short {
      display: block;
      white-space: pre;
      overflow-wrap: normal;
      font-size: 11px;
    }
  }

  &__cell--empty &__hours {
    color: rgba(0, 0, 0, 0.25);
  }

  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 12px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 12px;
  }

  &__summary {
    margin-left: auto;
    text-align: right;
  }

  &__legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__editor {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 260px;
    max-width: calc(100vw - 48px);

    :deep(.ant-select-selector) { padding-inline: 30px; }
    :deep(.ant-select-selection-item) {
      padding-inline-end: 0;
      text-align: center;
    }
  }

  &__time-range {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    :deep(.ant-picker) { width: 100%; min-width: 0; }
    :deep(.ant-picker-input > input) { text-align: center; }
    :deep(.ant-picker-suffix) { position: absolute; right: 10px; }
  }

  &--compact &__weekdays,
  &--compact &__grid {
    gap: 3px;
  }

  &--compact &__weekdays {
    margin-bottom: 3px;
  }

  &--compact &__weekday {
    font-size: 10px;
  }

  &--compact &__gap,
  &--compact &__cell {
    min-height: 40px;
  }

  &--compact &__cell {
    padding: 3px 4px;
    border-radius: 4px;
  }

  &--compact &__number {
    font-size: 11px;
  }

  &--compact &__hours {
    font-size: 10px;
    line-height: 1.2;
    white-space: pre-line;
  }
}
</style>
